import type { Invoice, Money, TaxKind } from "@estelle/domain";

import { sumMoney } from "@/modules/gst/format";

/** Taxable value and tax totals for one `TaxKind`. */
export interface TaxKindSummary {
  taxKind: TaxKind;
  invoiceCount: number;
  taxableValue: Money;
  cgstAmount: Money;
  sgstAmount: Money;
  igstAmount: Money;
  cessAmount: Money;
  total: Money;
}

/**
 * Taxable value and tax aggregated by tax kind (FR-GST-14 / ticket #96) —
 * the CGST+SGST vs. IGST split a return files against.
 *
 * Only `issued` invoices count, for the same reason `aggregateHsnSummary`
 * excludes `draft` and `cancelled`: a draft never charged tax and a
 * cancelled invoice's charge was voided, so counting either overstates what
 * a return actually owes.
 */
export function aggregateByTaxKind(
  invoices: readonly Invoice[],
): TaxKindSummary[] {
  const groups = new Map<TaxKind, Invoice[]>();

  for (const invoice of invoices) {
    if (invoice.status !== "issued") continue;
    const group = groups.get(invoice.taxKind);
    if (group) {
      group.push(invoice);
    } else {
      groups.set(invoice.taxKind, [invoice]);
    }
  }

  return Array.from(groups.entries()).map(([taxKind, group]) => ({
    taxKind,
    invoiceCount: group.length,
    taxableValue: sumMoney(group.map((invoice) => invoice.totalTaxableValue)),
    cgstAmount: sumMoney(group.map((invoice) => invoice.totalCgst)),
    sgstAmount: sumMoney(group.map((invoice) => invoice.totalSgst)),
    igstAmount: sumMoney(group.map((invoice) => invoice.totalIgst)),
    cessAmount: sumMoney(group.map((invoice) => invoice.totalCess)),
    total: sumMoney(group.map((invoice) => invoice.total)),
  }));
}

export function taxKindLabel(taxKind: TaxKind): string {
  return taxKind === "igst" ? "IGST" : "CGST + SGST";
}
